import React, {useEffect, useRef, useState} from "react";
import {create} from "simple-drawing-board";
import {post, getJSON, page, ghostBtn, Countdown} from "./ui";
import {ZedSays} from "./zed";

const CANVAS = 500;
const LINE = 10;
const COLORS = ['#FFFFFF', '#EE204D', '#FFDC00', '#1F75FE', '#B5674D', '#FF7538', '#1CAC78', '#925EAE'];

const smearMask = {
    WebkitMaskImage: 'url(/dancelestrations-assets/smear.png)', maskImage: 'url(/dancelestrations-assets/smear.png)',
    WebkitMaskSize: '100% 100%', maskSize: '100% 100%',
    WebkitMaskRepeat: 'no-repeat', maskRepeat: 'no-repeat',
    WebkitMaskPosition: 'center', maskPosition: 'center',
};

export const DrawPhase = ({state, offset}) => {
    const [task, setTask] = useState(null);
    useEffect(() => {
        let live = true;
        getJSON('/demo/my-draw').then(t => { if (live) setTask(t); });
        return () => { live = false; };
    }, [state.round]);

    if (!task) return <div style={{...page, alignItems: 'center', justifyContent: 'center'}}>…</div>;
    return <Draw task={task} state={state} offset={offset} />;
};

const Draw = ({task, state, offset}) => {
    const [color, setColor] = useState('#FFFFFF');
    const sdbRef = useRef(null);
    const colorRef = useRef('#FFFFFF');
    const startRef = useRef(null);
    const eventsRef = useRef([]);
    const curRef = useRef(null);

    useEffect(() => { colorRef.current = color; }, [color]);

    // Save continuously (after each stroke) so whenever the timer fires the
    // server already has the latest drawing — no lost work, no submit button.
    const save = () => {
        const recording = {v: 1, w: CANVAS, h: CANVAS, events: eventsRef.current};
        post('/demo/save-drawing', {recording: JSON.stringify(recording)}).catch(() => {});
    };

    useEffect(() => {
        const canvas = document.getElementById('demo-draw');
        const sdb = create(canvas);
        sdb.setLineSize(LINE);
        sdb.setLineColor('#FFFFFF');
        startRef.current = null; eventsRef.current = []; curRef.current = null;

        const ts = () => {
            if (startRef.current === null) startRef.current = performance.now();
            return Math.round(performance.now() - startRef.current);
        };
        const onBegin = ({x, y}) => { curRef.current = {t: 'stroke', ts: ts(), c: colorRef.current, s: LINE, pts: [[Math.round(x), Math.round(y)]]}; };
        const onDraw = ({x, y}) => { if (curRef.current) curRef.current.pts.push([Math.round(x), Math.round(y)]); };
        const onEnd = () => { if (curRef.current) { eventsRef.current.push(curRef.current); curRef.current = null; save(); } };

        sdb.observer.on('drawBegin', onBegin);
        sdb.observer.on('draw', onDraw);
        sdb.observer.on('drawEnd', onEnd);
        sdbRef.current = sdb;

        return () => {
            sdb.observer.off('drawBegin', onBegin);
            sdb.observer.off('draw', onDraw);
            sdb.observer.off('drawEnd', onEnd);
            save();
            sdb.destroy();
        };
    }, [task.word]); // eslint-disable-line react-hooks/exhaustive-deps

    const recordEvent = (t) => {
        if (startRef.current === null) startRef.current = performance.now();
        eventsRef.current.push({t, ts: Math.round(performance.now() - startRef.current)});
        save();
    };
    const undo = () => { sdbRef.current && sdbRef.current.undo(); recordEvent('undo'); };
    const clear = () => { sdbRef.current && sdbRef.current.clear(); recordEvent('clear'); };
    const setCol = (c) => { setColor(c); if (sdbRef.current) sdbRef.current.setLineColor(c); };

    // The drawing tools sit against the bottom edge; Zed is centered in whatever room is left above them.
    return <div style={{...page, gap: 12}}>
        <Countdown endsAt={state.phase_ends_at} offset={offset} />
        <div style={{flex: 1, display: 'flex', alignItems: 'center'}}>
            <ZedSays line={`Draw this: ${task.word}.`} />
        </div>

        <canvas id="demo-draw" width={CANVAS} height={CANVAS}
                style={{width: '100%', aspectRatio: '1 / 1', height: 'auto', backgroundColor: '#0a0a0a', border: '1.5px solid rgba(176,141,87,0.55)', borderRadius: 12, touchAction: 'none'}} />

        <div style={{display: 'flex', justifyContent: 'space-between', gap: 4, alignItems: 'center', padding: '10px 12px', backgroundColor: 'rgba(16,13,11,0.92)', border: '1.5px solid rgba(176,141,87,0.55)', borderRadius: 12}}>
            {COLORS.map((c, i) => {
                const isSelected = color === c;
                const rotation = (i * 13) % 12 - 6;
                return <div key={c} onClick={() => setCol(c)} style={{flex: 1, aspectRatio: '1 / 1', position: 'relative', cursor: 'pointer', transform: isSelected ? 'translateY(-8px)' : 'translateY(0)', transition: 'transform 120ms'}}>
                    {isSelected && <div style={{position: 'absolute', inset: '-6%', borderRadius: '50%', backgroundColor: 'black', border: '2px solid white', boxShadow: '3px 3px 0 rgba(0,0,0,0.7)'}} />}
                    <div style={{position: 'absolute', inset: 0, backgroundColor: c, transform: `rotate(${rotation}deg) scale(${isSelected ? 1.05 : 0.9})`, filter: isSelected ? 'none' : 'drop-shadow(2px 2px 0 rgba(0,0,0,0.7))', transition: 'transform 120ms', ...smearMask}} />
                </div>;
            })}
        </div>

        <div style={{display: 'flex', gap: 12}}>
            <button style={ghostBtn} onClick={undo}>↶ Undo</button>
            <button style={ghostBtn} onClick={clear}>✕ Clear</button>
        </div>
    </div>;
};
